// ── Denomination line ─────────────────────────────────────────────────────────

export interface CashReconciliationLineDto {
  id:                   string;
  cashDenominationId?:  string | null;
  denominationValue:    number;
  denominationType:     'Note' | 'Coin';
  count:                number;
  amount:               number;
  currencyCode:         string;
  sortOrder:            number;
}

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface CashReconciliationDto {
  id:                        string;
  reconciliationNumber:      string;
  reconciliationDate:        string; // DateOnly as "YYYY-MM-DD"
  currencyCode:              string;
  exchangeRate:              number;
  shiftCode?:                string | null;
  locationId?:               string | null;
  cashAccountId?:            string | null;
  openingFloat:              number;
  cashSalesAmount:           number;
  cashReceiptsAmount:        number;
  cashPayoutsAmount:         number;
  pettyCashAmount:           number;
  expectedClosingBalance:    number;
  actualCountedAmount:       number;
  variance:                  number;
  baseVariance:              number;
  status:                    string;
  postingStatus:             string;
  varianceReason?:           string | null;
  requiresVarianceApproval:  boolean;
  varianceApprovalStatus?:   string | null;
  varianceGlAccountCode?:    string | null;
  varianceJournalId?:        string | null;
  glJournalId?:              string | null;
  remarks?:                  string | null;
  denominationLines:         CashReconciliationLineDto[];
  createdDate:               string;
  createdBy?:                string | null;
  modifiedDate?:             string | null;
  modifiedBy?:               string | null;
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface CashReconciliationListDto {
  id:                     string;
  reconciliationNumber:   string;
  reconciliationDate:     string;
  currencyCode:           string;
  expectedClosingBalance: number;
  actualCountedAmount:    number;
  variance:               number;
  status:                 string;
  postingStatus:          string;
  shiftCode?:             string | null;
  createdDate:            string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface CashReconciliationFilterDto {
  companyId:    string;
  dateFrom?:    string;
  dateTo?:      string;
  status?:      string;
  currencyCode?: string;
  locationId?:  string;
  page?:        number;
  pageSize?:    number;
}

// ── Create request ────────────────────────────────────────────────────────────

export interface CreateCashReconciliationRequest {
  reconciliationDate: string;
  currencyCode:       string;
  exchangeRate:       number;
  shiftCode?:         string;
  locationId?:        string;
  cashAccountId?:     string;
  openingFloat:       number;
  remarks?:           string;
}

// ── Action requests ───────────────────────────────────────────────────────────

export interface RecordCashAmountsRequest {
  cashSalesAmount:    number;
  cashReceiptsAmount: number;
  cashPayoutsAmount:  number;
  pettyCashAmount:    number;
}

export interface RecordDenominationLineRequest {
  cashDenominationId?:  string;
  denominationValue:    number;
  denominationType:     string;
  count:                number;
  currencyCode:         string;
  sortOrder:            number;
}

export interface RecordActualCountRequest {
  actualCountedAmount: number;
  varianceReason?:     string;
  denominationLines?:  RecordDenominationLineRequest[];
}

export interface ApproveVarianceRequest {
  remarks?: string;
}

export interface RejectVarianceRequest {
  reason: string;
}

export interface RejectCashReconciliationRequest {
  reason: string;
}

// ── Status display helpers ────────────────────────────────────────────────────

export const CASH_RECON_STATUS_COLORS: Record<string, string> = {
  Draft:     'bg-slate-100 text-slate-600',
  Submitted: 'bg-blue-100 text-blue-700',
  Approved:  'bg-yellow-100 text-yellow-700',
  Posted:    'bg-green-100 text-green-700',
  Rejected:  'bg-red-100 text-red-700',
};

export const CASH_RECON_STATUS_LABELS: Record<string, string> = {
  Draft:     'Draft',
  Submitted: 'Submitted',
  Approved:  'Approved',
  Posted:    'Posted',
  Rejected:  'Rejected',
};
